'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["message", "confirmLabel", "cancelLabel", "onConfirm", "onCancel", "className", "classes", "sx"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatConfirmationUtilityClasses } from "./chatConfirmationClasses.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatConfirmation');

// Inline SVG — avoids @mui/icons-material dependency
function WarningIcon() {
  return /*#__PURE__*/_jsx("svg", {
    viewBox: "0 0 24 24",
    fill: "currentColor",
    width: "1.25em",
    height: "1.25em",
    "aria-hidden": "true",
    children: /*#__PURE__*/_jsx("path", {
      d: "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
    })
  });
}
const ChatConfirmationRoot = styled('div', {
  name: 'MuiChatConfirmation',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root
})(({
  theme
}) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${(theme.vars || theme).palette.warning.main}`,
  padding: theme.spacing(2),
  backgroundColor: (theme.vars || theme).palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5)
}));
const ChatConfirmationIcon = styled('span', {
  name: 'MuiChatConfirmation',
  slot: 'Icon',
  overridesResolver: (_, styles) => styles.icon
})(({
  theme
}) => ({
  display: 'flex',
  color: (theme.vars || theme).palette.warning.main,
  fontSize: 20
}));
const ChatConfirmationMessage = styled('p', {
  name: 'MuiChatConfirmation',
  slot: 'Message',
  overridesResolver: (_, styles) => styles.message
})(({
  theme
}) => _extends({}, theme.typography.body2, {
  color: (theme.vars || theme).palette.text.primary,
  margin: 0
}));
const ChatConfirmationActions = styled('div', {
  name: 'MuiChatConfirmation',
  slot: 'Actions',
  overridesResolver: (_, styles) => styles.actions
})(({
  theme
}) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'flex-end'
}));
const ChatConfirmationCancelButton = styled('button', {
  name: 'MuiChatConfirmation',
  slot: 'CancelButton',
  overridesResolver: (_, styles) => styles.cancelButton
})(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.625, 1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
  backgroundColor: 'transparent',
  color: (theme.vars || theme).palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  fontFamily: theme.typography.fontFamily,
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color']),
  '&:hover': {
    backgroundColor: (theme.vars || theme).palette.action.hover
  },
  '&:focus-visible': {
    outline: `2px solid ${(theme.vars || theme).palette.primary.main}`,
    outlineOffset: 2
  }
}));
const ChatConfirmationConfirmButton = styled('button', {
  name: 'MuiChatConfirmation',
  slot: 'ConfirmButton',
  overridesResolver: (_, styles) => styles.confirmButton
})(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.625, 1.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${(theme.vars || theme).palette.warning.main}`,
  backgroundColor: (theme.vars || theme).palette.warning.main,
  color: (theme.vars || theme).palette.warning.contrastText,
  fontSize: theme.typography.body2.fontSize,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightMedium,
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'border-color']),
  '&:hover': {
    backgroundColor: (theme.vars || theme).palette.warning.dark,
    borderColor: (theme.vars || theme).palette.warning.dark
  },
  '&:focus-visible': {
    outline: `2px solid ${(theme.vars || theme).palette.warning.main}`,
    outlineOffset: 2
  }
}));
const ChatConfirmation = /*#__PURE__*/React.forwardRef(function ChatConfirmation(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatConfirmation'
  });
  const {
      message,
      confirmLabel = 'Confirm',
      cancelLabel = 'Cancel',
      onConfirm,
      onCancel,
      className,
      classes: classesProp,
      sx
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatConfirmationUtilityClasses(classesProp);
  const messageId = React.useId();
  return /*#__PURE__*/_jsxs(ChatConfirmationRoot, _extends({
    ref: ref,
    className: clsx(classes.root, className),
    sx: sx
    // Inline, non-modal confirmation card: a labelled `group` (named by its
    // message) rather than `alertdialog`, which would promise modal focus
    // management this card neither provides nor needs.
    ,
    role: "group",
    "aria-labelledby": messageId
  }, other, {
    children: [/*#__PURE__*/_jsx(ChatConfirmationIcon, {
      className: classes.icon,
      children: /*#__PURE__*/_jsx(WarningIcon, {})
    }), /*#__PURE__*/_jsx(ChatConfirmationMessage, {
      id: messageId,
      className: classes.message,
      children: message
    }), /*#__PURE__*/_jsxs(ChatConfirmationActions, {
      className: classes.actions,
      children: [/*#__PURE__*/_jsx(ChatConfirmationCancelButton, {
        type: "button",
        className: classes.cancelButton,
        onClick: onCancel,
        children: cancelLabel
      }), /*#__PURE__*/_jsx(ChatConfirmationConfirmButton, {
        type: "button",
        className: classes.confirmButton,
        onClick: onConfirm,
        children: confirmLabel
      })]
    })]
  }));
});
if (process.env.NODE_ENV !== "production") ChatConfirmation.displayName = "ChatConfirmation";
process.env.NODE_ENV !== "production" ? ChatConfirmation.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * Label for the cancel button.
   * @default 'Cancel'
   */
  cancelLabel: PropTypes.string,
  classes: PropTypes.object,
  className: PropTypes.string,
  /**
   * Label for the confirm button.
   * @default 'Confirm'
   */
  confirmLabel: PropTypes.string,
  /**
   * The question or warning message to display. Required.
   */
  message: PropTypes.string.isRequired,
  /**
   * Called when the user clicks the cancel button.
   */
  onCancel: PropTypes.func,
  /**
   * Called when the user clicks the confirm button.
   */
  onConfirm: PropTypes.func,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatConfirmation };